import React from 'react';
import _ from 'lodash';
import {Tree, TreeItem} from "../ui/react-aria/Tree";
import type {Key, Selection} from 'react-aria-components';
import {connect} from "react-redux";
import {withStyles} from "material-ui/styles";
import styles from "./JobsViewByTreeViewStyles";
import {
  customerList, getCustomer, getDevices,
  setJobsViewBy,
  treeview
} from "../../redux/actions";
import {TreeNode} from "./models";
import {isNaviTEKType, isSignalTEKQTType, isVDVType} from "../../common/utils";

interface TreeNodeType {
  title: string;
  jobIds: number[];
}

type TreeNodeTypes = Map<string, TreeNodeType[]>;

const rootTreeNodes: TreeNode[] = [
  {
    id: 'customers',
    type: 'customers',
    title: 'Customers',
    checked: false,
    jobIds: [],
    children: []
  },
  {
    id: 'deviceTypes',
    type: 'deviceTypes',
    title: 'Device Types',
    checked: false,
    jobIds: [],
    children: []
  },
  {
    id: 'buildings',
    type: 'buildings',
    title: 'Buildings',
    checked: false,
    jobIds: [],
    children: []
  },
  {
    id: 'floors',
    type: 'floors',
    title: 'Floors',
    checked: false,
    jobIds: [],
    children: []
  },
  {
    id: 'rooms',
    type: 'rooms',
    title: 'Rooms',
    checked: false,
    jobIds: [],
    children: []
  },
  {
    id: 'racks',
    type: 'racks',
    title: 'Racks',
    checked: false,
    jobIds: [],
    children: []
  },
  {
    id: 'panels',
    type: 'panels',
    title: 'Panels',
    checked: false,
    jobIds: [],
    children: []
  },
];

const rootTreeTranslationKeys: {[key: string]: string} = {
  customers: 'view_by_customers',
  deviceTypes: 'view_by_device_types',
  buildings: 'view_by_buildings',
  floors: 'view_by_floors',
  rooms: 'view_by_rooms',
  racks: 'view_by_racks',
  panels: 'view_by_panels',
};

const customerPager = {
  pageno: 0,
  pagesize: 0
};

class JobsViewByTreeView extends React.Component<any, any> {
  constructor(props: any) {
    super(props);
    this.state = {
      treeNodes: rootTreeNodes,
      selectedTreeItems: new Set<Key>(),
      organisation: null
    };
    this.handleTreeSelectionChange = this.handleTreeSelectionChange.bind(this);
  }

  componentDidMount() {
    const {getCustomer, getCustomers, getDevices, getTreeData, profile} = this.props;
    getCustomer(profile.customerid);
    getCustomers(customerPager);
    getDevices();
    getTreeData('all', -1);
  }

  componentDidUpdate(prevProps: any, prevState: any) {
    const {customers, devices, treeData, jobsViewBy, customer} = this.props;

    if (prevProps.customer !== customer || prevProps.customers !== customers) {
      this.customersChanged(customers, customer);
    }

    if (prevProps.devices !== devices) {
      this.deviceTypesChanged(devices);
    }

    if (prevProps.treeData !== treeData) {
      this.treeDataChanged(treeData);
    }

    if (prevProps.jobsViewBy !== jobsViewBy) {
      this.jobsViewByChanged(jobsViewBy);
    }
  }

  customersChanged(customers, customer) {
    if (customers && customers.data && customers.data.length && customer) {
      const {treeNodes} = this.state;
      const node = treeNodes.find((i: TreeNode) => i.type === 'customers');
      if (node) {
        const customerItems = customers.data.map((c: any) => {
          return {
            id: c.id,
            name: c.name,
          }
        });
        const customerItem = {
          id: customer.id,
          name: customer.name,
        }
        const children = [...customerItems, customerItem];
        node.children = children.map((c: any) => {
          const item: TreeNode = {
            id: `customer-${c.id}`,
            type: 'customer',
            title: c.name,
            checked: false,
            nodeId: c.id,
            jobIds: [],
            children: []
          };
          return item;
        });

        this.applyJobsViewBySelection(treeNodes);
      }
    }
  }

  getDeviceTypes(deviceTypes) {
    deviceTypes.find(x => x.id === "lantekiii")
      ? null
      : deviceTypes.push({
        id: "lantekiii",
        name: "LantekIII"
      });

    let stqtEnabled = deviceTypes.filter(x => isSignalTEKQTType(x.id)).length > 0;
    let navitekEnabled = deviceTypes.filter(x => isNaviTEKType(x.id)).length > 0;
    let displayDeviceTypes = deviceTypes.filter(x => {
      return !isVDVType(x.id) && !isSignalTEKQTType(x.id) && !isNaviTEKType(x.id);
    });

    displayDeviceTypes.push({
      id: "vdviipoepro",
      name: "VDV II / PoE Pro"
    });

    if (stqtEnabled) {
      displayDeviceTypes.push({
        id: "signaltekqtorpro",
        name: "SignalTEK QT / SignalTEK QT Pro"
      });
    }

    if (navitekEnabled) {
      displayDeviceTypes.push({
        id: "navitekorpro",
        name: "NaviTEK / NaviTEK Pro"
      });
    }

    return displayDeviceTypes.filter(device => device.id !== "1_g");
  }

  deviceTypesChanged(devices: any) {
    const {translate} = this.props;

    if (devices && devices.length) {
      let deviceTypes = this.getDeviceTypes([...devices]);
      deviceTypes = _.sortBy(deviceTypes, ['name']);

      const {treeNodes} = this.state;
      const node = treeNodes.find((i: TreeNode) => i.type === 'deviceTypes');
      if (node) {
        node.children = deviceTypes.map((i: any) => {
          const item: TreeNode = {
            id: `deviceType-${i.id}`,
            type: 'deviceType',
            title: translate(i.id),
            checked: false,
            nodeId: i.id,
            jobIds: [],
            children: []
          };
          return item;
        });

        this.applyJobsViewBySelection(treeNodes);
      }
    }
  }

  treeDataChanged(treeData: any) {
    if (treeData && treeData.length) {
      const jobs = treeData.find((i: any) => i.type === 'jobs');
      if (jobs && jobs.children && jobs.children.length) {
        const treeNodeTypes: TreeNodeTypes = new Map<string, TreeNodeType[]>([
          ["panel", []],
          ["rack", []],
          ["room", []],
          ["floor", []],
          ["building", []],
        ]);
        this.parseTreeData(jobs.children, treeNodeTypes);

        const {treeNodes} = this.state;
        this.addTreeNodes(treeNodes, 'buildings', 'building', treeNodeTypes.get('building'));
        this.addTreeNodes(treeNodes, 'floors', 'floor', treeNodeTypes.get('floor'));
        this.addTreeNodes(treeNodes, 'rooms', 'room', treeNodeTypes.get('room'));
        this.addTreeNodes(treeNodes, 'racks', 'rack', treeNodeTypes.get('rack'));
        this.addTreeNodes(treeNodes, 'panels', 'panel', treeNodeTypes.get('panel'));
        this.applyJobsViewBySelection(treeNodes);
      }
    }
  }

  jobsViewByChanged(jobsViewBy: TreeNode[]) {
    this.applyJobsViewBySelection(this.state.treeNodes, jobsViewBy);
  }

  applyJobsViewBySelection(treeNodes: TreeNode[], jobsViewBy: TreeNode[] = this.props.jobsViewBy) {
    const selectedIds = (jobsViewBy || []).map(i => i.id);
    this.setSelectedTreeNodes(treeNodes, selectedIds);
    this.updateTreeNodes(treeNodes);
    this.updateSelectedTreeNodes(treeNodes);
  }

  updateTreeNodes(treeNodes: TreeNode[]) {
    this.setState({treeNodes: [...treeNodes]});
  }

  addTreeNodes(treeNodes: TreeNode[], parentType: string, itemType: string, items: TreeNodeType[] | undefined) {
    if (items) {
      const parentItem = treeNodes.find((i: TreeNode) => i.type === parentType);
      if (parentItem) {
        let index = 0;
        parentItem.children = items
          .sort((a, b) => a.title.localeCompare(b.title))
          .map((i: TreeNodeType) => {
            index++;
            const item: TreeNode = {
              id: `${itemType}-${index}`,
              type: itemType,
              title: i.title,
              checked: false,
              jobIds: i.jobIds,
              children: []
            };
            return item;
          });
      }
    }
  }

  addTreeNodeType(nodeType: string, title: string, jobId: number, treeNodeTypes: TreeNodeTypes) {
    const items = treeNodeTypes.get(nodeType);
    if (items) {
      const item = items.find(i => i.title === title);
      if (item) {
        if (item.jobIds.indexOf(jobId) === -1) {
          item.jobIds.push(jobId);
        }
      } else {
        items.push({title: title, jobIds: [jobId]});
      }
    }
  }

  parseTreeData(items: any, treeNodeTypes: TreeNodeTypes) {
    if (items && items.length) {
      items.forEach((item: any) => {
        if (['panel', 'rack', 'room', 'floor', 'building'].indexOf(item.type) !== -1) {
          this.addTreeNodeType(item.type, item.title, item.jobId, treeNodeTypes);
        }

        this.parseTreeData(item.children, treeNodeTypes);
      });
    }
  }

  renderTreeItems(items: TreeNode[], level: number) {
    const {translate} = this.props;
    return (
      <React.Fragment>
        {items && items.map((item: TreeNode) =>
          <TreeItem title={rootTreeTranslationKeys[item.id] ? translate(rootTreeTranslationKeys[item.id]) : item.title} key={item.id} id={item.id}
                    style={{'--tree-item-level': `${level}`} as React.CSSProperties}>
            {this.renderTreeItems(item.children, level + 1)}
          </TreeItem>
        )}
      </React.Fragment>
    );
  }

  findTreeNodeById(items: TreeNode[], id: string): TreeNode | null {
    for (const item of items) {
      if (item.id === id) {
        return item;
      } else {
        const found = this.findTreeNodeById(item.children, id);
        if (found) {
          return found;
        }
      }
    }

    return null;
  }

  selectTreeNode(treeNodes: TreeNode[], id: string, checked: boolean): boolean {
    const item = this.findTreeNodeById(treeNodes, id);
    if (item) {
      item.checked = checked;

      for (const child of item.children) {
        child.checked = checked;
      }

      return true;
    }

    return false;
  }

  getSelectedTreeNodes(items: TreeNode[]): TreeNode[] {
    const selectedItems: TreeNode[] = [];

    for (const item of items) {
      if (item.checked) {
        selectedItems.push(item);
      }

      selectedItems.push(...this.getSelectedTreeNodes(item.children));
    }

    return selectedItems;
  }

  updateSelectedTreeNodes(treeNodes: TreeNode[]) {
    const selectedKeys = new Set<Key>();
    for (const item of this.getSelectedTreeNodes(treeNodes)) {
      selectedKeys.add(item.id);
    }

    this.setSelectedTreeItems(selectedKeys);
  }

  setSelectedTreeItems(keys: Set<Key>) {
    this.setState({selectedTreeItems: keys});
  }

  handleTreeSelectionChange(keys: Selection) {
    const {selectedTreeItems} = this.state;

    if (keys !== "all") {
      const {treeNodes} = this.state;
      const selectedIds = Array.from(keys);
      const alreadySelectedIds: Key[] = Array.from(selectedTreeItems);

      const idsToSelect: Key[] = _.difference(selectedIds, alreadySelectedIds);
      for (const id of idsToSelect) {
        this.selectTreeNode(treeNodes, id as string, true);
      }

      const idsToUnselect: Key[] = _.difference(alreadySelectedIds, selectedIds);
      for (const id of idsToUnselect) {
        this.selectTreeNode(treeNodes, id as string, false);
      }

      this.updateTreeNodes(treeNodes);
      this.updateJobsViewBy(treeNodes);
    }
  }

  updateJobsViewBy(treeNodes: TreeNode[]) {
    const selectedTreeNodes = this.getSelectedTreeNodes(treeNodes)
      .filter(n => n.children.length === 0);
    this.props.setJobsViewBy(selectedTreeNodes);
  }

  setSelectedTreeNodes(treeNodes: TreeNode[], selectedIds: string[]) {
    for (const node of treeNodes) {
      if (node.children.length === 0) {
        node.checked = selectedIds.indexOf(node.id) !== -1;
      } else {
        this.setSelectedTreeNodes(node.children, selectedIds);

        if (node.children.some(i => !i.checked)) {
          node.checked = false;
        } else if (node.children.every(i => i.checked)) {
          node.checked = true;
        }
      }
    }
  }

  render() {
    const {treeNodes, selectedTreeItems} = this.state;
    const {classes, translate} = this.props;

    return <div className={classes.root}>
      <Tree
        aria-label={translate('view_by')}
        items={treeNodes}
        selectionMode="multiple"
        selectedKeys={selectedTreeItems}
        onSelectionChange={this.handleTreeSelectionChange}>
        {this.renderTreeItems(treeNodes, 1)}
      </Tree>
    </div>;
  }
}

const mapStateToProps = (state: any) => ({
  profile: state.profileReducer.profile,
  customer: state.customerReducer.customer,
  customers: state.customerReducer.customers,
  jobsViewBy: state.jobViewByReducer.jobsViewBy,
  treeData: state.treeviewReducer.treeData,
  devices: state.jobReducer.devices,
  ...state
});

const mapDispatchToProps = (dispatch: any) => ({
  getCustomers: (pager: any) => {
    dispatch(customerList(pager));
  },
  setJobsViewBy: (treeNodes: TreeNode[]) => {
    dispatch(setJobsViewBy(treeNodes));
  },
  getTreeData: (props: any) => {
    dispatch(treeview(props));
  },
  getDevices: () => {
    dispatch(getDevices());
  },
  getCustomer: id => {
    dispatch(getCustomer(id));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)
(withStyles(styles)(JobsViewByTreeView));
